<template>
  <HeaderComponent />
  
  <HeroComponent 
    image="https://images.pexels.com/photos/5668774/pexels-photo-5668774.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
    text="Terms of Use" 
    subtitle="Please read these terms carefully before using our services"
  />

  <!-- Terms Content -->
  <div class="py-20 bg-gray-50">
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto">
        
        <!-- Last Updated -->
        <div class="bg-white rounded-2xl shadow-lg p-8 mb-8" data-sal="slide-up" data-sal-delay="200">
          <p class="text-gray-600 text-center">
            <strong>Last Updated:</strong> {{ lastUpdated }}
          </p>
        </div>

        <!-- Terms Sections -->
        <div class="bg-white rounded-3xl shadow-lg p-8 md:p-12" data-sal="slide-up" data-sal-delay="400">
          <div class="prose prose-lg max-w-none">
            
            <!-- Introduction -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">1. Introduction</h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                Welcome to DIGINOVA ("we," "our," or "us"). These Terms of Use ("Terms") govern your use of our website, services, and products. By accessing or using our services, you agree to be bound by these Terms.
              </p>
              <p class="text-gray-600 leading-relaxed">
                If you do not agree with any part of these Terms, please do not use our services.
              </p>
            </section>

            <!-- Services -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">2. Our Services</h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                DIGINOVA provides digital design and development services including but not limited to:
              </p>
              <ul class="text-gray-600 leading-relaxed mb-4 pl-6">
                <li class="mb-2">• Brand identity design and strategy</li>
                <li class="mb-2">• Website design and development</li>
                <li class="mb-2">• User experience (UX) and user interface (UI) design</li>
                <li class="mb-2">• Digital marketing materials</li>
                <li class="mb-2">• Ongoing website maintenance and support</li>
              </ul>
              <p class="text-gray-600 leading-relaxed">
                We reserve the right to modify, suspend, or discontinue any aspect of our services at any time.
              </p>
            </section>

            <!-- Client Responsibilities -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">3. Client Responsibilities</h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                As a client, you agree to:
              </p>
              <ul class="text-gray-600 leading-relaxed mb-4 pl-6">
                <li class="mb-2">• Provide accurate and complete information necessary for project completion</li>
                <li class="mb-2">• Respond to requests for feedback and approvals within agreed timeframes</li>
                <li class="mb-2">• Make payments according to the agreed schedule</li>
                <li class="mb-2">• Ensure you have the rights to all content you provide</li>
                <li class="mb-2">• Use our services in compliance with all applicable laws and regulations</li>
              </ul>
            </section>

            <!-- Payment Terms -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">4. Payment Terms</h2>
              <div class="space-y-4">
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">4.1 Payment Schedule</h3>
                  <p class="text-gray-600 leading-relaxed">
                    Unless otherwise agreed, projects require a 50% deposit before work begins, with the remainder due upon completion.
                  </p>
                </div>
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">4.2 Late Payments</h3>
                  <p class="text-gray-600 leading-relaxed">
                    Late payments may incur a fee of 1.5% per month. Work may be suspended for accounts more than 30 days overdue.
                  </p>
                </div>
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">4.3 Refunds</h3>
                  <p class="text-gray-600 leading-relaxed">
                    Refunds are handled on a case-by-case basis. Completed work is generally non-refundable.
                  </p>
                </div>
              </div>
            </section>

            <!-- Intellectual Property -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">5. Intellectual Property</h2>
              <div class="space-y-4">
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">5.1 Client Ownership</h3>
                  <p class="text-gray-600 leading-relaxed">
                    Upon full payment, clients receive ownership of the final deliverables created specifically for their project.
                  </p>
                </div>
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">5.2 DIGINOVA Rights</h3>
                  <p class="text-gray-600 leading-relaxed">
                    We retain the right to use completed projects in our portfolio and marketing materials unless otherwise agreed in writing.
                  </p>
                </div>
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">5.3 Third-Party Materials</h3>
                  <p class="text-gray-600 leading-relaxed">
                    Any third-party materials (fonts, stock photos, etc.) remain subject to their respective licenses.
                  </p>
                </div>
              </div>
            </section>

            <!-- Limitation of Liability -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">6. Limitation of Liability</h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                DIGINOVA's liability is limited to the amount paid for the specific services in question. We are not liable for:
              </p>
              <ul class="text-gray-600 leading-relaxed mb-4 pl-6">
                <li class="mb-2">• Indirect, incidental, or consequential damages</li>
                <li class="mb-2">• Loss of profits, data, or business opportunities</li>
                <li class="mb-2">• Issues arising from third-party services or platforms</li>
                <li class="mb-2">• Client's use of deliverables after project completion</li>
              </ul>
            </section>

            <!-- Termination -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">7. Termination</h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                Either party may terminate a project with written notice. In case of termination:
              </p>
              <ul class="text-gray-600 leading-relaxed mb-4 pl-6">
                <li class="mb-2">• Client pays for all work completed to date</li>
                <li class="mb-2">• DIGINOVA delivers all completed work</li>
                <li class="mb-2">• Both parties are released from future obligations</li>
              </ul>
            </section>

            <!-- Governing Law -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">8. Governing Law</h2>
              <p class="text-gray-600 leading-relaxed">
                These Terms are governed by the laws of Switzerland. Any disputes will be resolved through the courts of Switzerland.
              </p>
            </section>

            <!-- Changes to Terms -->
            <section class="mb-12">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">9. Changes to Terms</h2>
              <p class="text-gray-600 leading-relaxed">
                We reserve the right to update these Terms at any time. Significant changes will be communicated to active clients. Continued use of our services constitutes acceptance of updated Terms.
              </p>
            </section>

            <!-- Contact -->
            <section class="mb-8">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">10. Contact Information</h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              <div class="bg-gray-50 rounded-lg p-6">
                <p class="text-gray-600"><strong>Email:</strong> contact@diginova.ch</p>
                <p class="text-gray-600"><strong>Address:</strong> Switzerland</p>
              </div>
            </section>
          </div>
        </div>

        <!-- Contact CTA -->
        <div class="text-center mt-12 bg-white rounded-3xl p-8 shadow-lg" data-sal="slide-up" data-sal-delay="600">
          <h3 class="text-2xl font-bold text-gray-900 mb-4">Questions about our Terms?</h3>
          <p class="text-gray-600 mb-6">
            Our team is here to clarify any questions you may have about our terms of service.
          </p>
          <router-link 
            to="/contact" 
            class="inline-block bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Contact Us
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <FooterComponent />
</template>

<script>
import HeaderComponent from '@/components/Header.vue'
import FooterComponent from '@/components/Footer.vue'
import HeroComponent from '@/components/Hero.vue'

export default {
  name: 'TermsView',
  components: {
    HeaderComponent,
    FooterComponent,
    HeroComponent
  },
  data() {
    return {
      lastUpdated: 'January 1, 2025'
    };
  },
  mounted() {
    if (typeof sal !== 'undefined') {
      sal({
        duration: 500,
        threshold: 0.1,
        once: true
      });
    }
  }
}
</script>

<style scoped>
.prose h2 {
  border-bottom: 1px solid #d1d5db;
  padding-bottom: 0.5rem;
}

.prose h3 {
  color: var(--color-primary);
}

.prose ul {
  list-style: none;
}
</style>